import React, { useEffect, useState } from "react";
import {
  Col,
  Form,
  Row,
  Button,
  Spinner,
  Card,
  Container,
} from "react-bootstrap";
import { getHooksGeneratorResponse } from "../../../services/getAiResponse";
import { ToolsHeader } from "../../components/ToolsHeader";
import { isInIframe } from "../../../utils/is-in-iframe";
import { Box } from "../hooksGeneratorStyle";

interface Hooks {
  topic: string;
  targetAudience: string;
}

interface HooksError {
  topic: boolean;
  targetAudience: boolean;
}

export default function HooksGenerators() {
  const [reloadPage, setReloadPage] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [validated, setValidated] = useState(false);
  const [inputData, setInputData] = useState<Hooks>({
    topic: "",
    targetAudience: "",
  });
  const [hooksResponse, setHooksResponse] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [errors, setErrors] = useState<HooksError>({
    topic: false,
    targetAudience: false,
  });

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [hooksResponse]);
  const handleReloadPage = () => {
    setHooksResponse("");
    setInputData({ topic: "", targetAudience: "" });
    setIsCopied(false);
    setReloadPage(true);
    setErrors({ topic: false, targetAudience: false });
  };

  const handleOnChange = (field: keyof Hooks, value: string) => {
    setInputData({ ...inputData, [field]: value });
    setErrors({
      ...errors,
      [field]: !value.trim(),
    });
  };

  const handleValidation = () => {
    const inputError: HooksError = {
      topic: !inputData.topic.trim(),
      targetAudience: !inputData.targetAudience.trim(),
    };
    setErrors(inputError);
    return !inputError.topic && !inputError.targetAudience;
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(hooksResponse)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (handleValidation()) {
      setShowLoader(true);
      try {
        const data = await getHooksGeneratorResponse(inputData);

        if (data) {
          setHooksResponse(data);
          setReloadPage(false);
          const height = document.body.scrollHeight;
          window.parent.postMessage({ type: "setHeight", height }, "*");
        }
      } catch (error) {
        console.error("Error fetching hooks:", error);
      } finally {
        setShowLoader(false);
      }
    }
  };

  return (
    <Box>
      <Container
        className={`${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"}`}
      >
        <Row>
          <Col>
            {!isInIframe && (
              <ToolsHeader
                title="Hooks Generators"
                description="Create captivating hooks that spark curiosity and engage your
                target audience instantly."
              />
            )}
            {/* <h1 className="text-center mb-3">Hooks Generators</h1>
        <p className="text-center mb-4">
          Create captivating hooks that grab attention and spark curiosity,
          tailored to your target audience.
        </p> */}
            <Card
              className="p-4 w-100"
              style={{
                maxWidth: "100%",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
              }}
            >
              <Form
                noValidate
                validated={validated}
                onSubmit={handleSubmit}
                id="hookGenerator"
              >
                <Row>
                  <Col>
                    <Form.Label>Topic</Form.Label>
                    <Form.Group className="mb-3" controlId="controlSummary">
                      <Form.Control
                        style={{
                          fontSize: "smaller",
                          height: "70px",
                        }}
                        value={inputData.topic}
                        placeholder='Enter your content topic (e.g., "A blog post about how to boost productivity without harming employee morale through output incentives")'
                        as="textarea"
                        onChange={(e) =>
                          handleOnChange("topic", e.target.value)
                        }
                        isInvalid={errors.topic}
                      />
                      <Form.Control.Feedback type="invalid">
                        Topic is required.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Label>Target Audience</Form.Label>
                    <Form.Group className="mb-3" controlId="controlAudience">
                      <Form.Control
                        style={{
                          fontSize: "smaller",
                          height: "70px",
                        }}
                        value={inputData.targetAudience}
                        placeholder='Describe your target audience (e.g., "small business owners," "tech-savvy professionals," "remote workers")'
                        as="textarea"
                        onChange={(e) =>
                          handleOnChange("targetAudience", e.target.value)
                        }
                        isInvalid={errors.targetAudience}
                      />
                      <Form.Control.Feedback type="invalid">
                        Target Audience is required.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col className="text-center">
                    {showLoader ? (
                      <Spinner animation="border" role="status" className="m-2">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      <Button className="m-2" style={{backgroundColor:"#8cc540"}} type="submit">
                        Submit
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>

              {!reloadPage && (
                <Row className="mt-4">
                  <Card
                    style={{
                      boxShadow:
                        "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
                    }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-end mb-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={handleReloadPage}
                        >
                          ↻ Reload
                        </Button>
                        <Button
                          variant={isCopied ? "success" : "outline-secondary"}
                          size="sm"
                          onClick={handleCopy}
                        >
                          {isCopied ? "✓ Copied" : "📋 Copy"}
                        </Button>
                      </div>
                      <pre style={{ whiteSpace: "pre-wrap" }}>
                        {hooksResponse}
                      </pre>
                    </Card.Body>
                  </Card>
                </Row>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </Box>
  );
}
